import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Loader2, FileDown, Building2 } from "lucide-react";
import { api } from "../../lib/api";
import HealthScoreTrend from "./HealthScoreTrend";

export default function BusinessHealthBoard() {
  const [clients, setClients] = useState(null);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/admin/health-scores")
      .then(({ data }) => {
        setClients(data.clients || []);
        if ((data.clients || []).length > 0) setSelected(data.clients[0].client_id);
      })
      .catch(() => toast.error("Could not load health scores."));
  }, []);

  const client = (clients || []).find((c) => c.client_id === selected);
  const latest = client?.trend?.[client.trend.length - 1];

  const downloadPdf = async () => {
    if (!latest?.id) return;
    setDownloading(true);
    try {
      const { data } = await api.get(`/admin/expenses/${latest.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Elevare-Health-Report-${(client.business_name || client.client_id).replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF.");
    } finally {
      setDownloading(false);
    }
  };

  if (clients === null) {
    return <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/50 py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading health scores…</div>;
  }

  if (clients.length === 0) {
    return (
      <div data-testid="health-board-empty" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 text-center">
        <Gauge className="h-8 w-8 mx-auto text-[#c9a961]" strokeWidth={1.5} />
        <p className="mt-3 text-sm text-[#1e3d3a]/60">No Business Health Scores yet. Run an Expense AI analysis for a client — each analysis scores their business across 8 categories.</p>
      </div>
    );
  }

  return (
    <div data-testid="health-board" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 items-start">
      {/* Client list */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-3 space-y-1.5">
        {clients.map((c) => {
          const last = c.trend[c.trend.length - 1];
          const avg = last?.scores?.length
            ? Math.round(last.scores.reduce((s, x) => s + (x.score || 0), 0) / last.scores.length)
            : 0;
          const active = c.client_id === selected;
          return (
            <button
              key={c.client_id}
              type="button"
              data-testid={`health-client-${c.client_id}`}
              onClick={() => setSelected(c.client_id)}
              className={`w-full text-left rounded-xl px-3.5 py-3 border transition-colors duration-300 ${
                active ? "border-[#c9a961] bg-[#c9a961]/10" : "border-transparent hover:border-[#1e3d3a]/15"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[#1e3d3a] truncate">{c.business_name || c.client_id}</span>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-[#a3823f]">{avg}</span>
              </div>
              <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 truncate">
                {c.client_id} · {c.trend.length} scan{c.trend.length === 1 ? "" : "s"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected client detail */}
      {client && (
        <div data-testid="health-board-detail" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6 space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#a3823f]" strokeWidth={1.7} />
                <span className="font-head text-xl text-[#1e3d3a]">{client.business_name || client.client_id}</span>
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
                {client.client_id} · Elevare Business Health Score™
              </div>
            </div>
            <button
              type="button"
              data-testid="health-download-pdf"
              onClick={downloadPdf}
              disabled={downloading || !latest?.id}
              className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
              Latest report PDF
            </button>
          </div>

          <HealthScoreTrend trend={client.trend} />

          {latest?.recommendation && (
            <div data-testid="health-board-recommendation" className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-4">
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Elevare's Recommendation</span>
              <p className="mt-2 text-sm leading-relaxed text-[#1e3d3a]/80">{latest.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
